import { valueof } from '@flipster/utils/typescript';

import { InstrumentProductTypeEnum } from '../snapshot/types';

export const MarketPlatformEnum = {
  Mobile: 'MOBILE',
  Web: 'WEB',
} as const;
export type MarketPlatform = valueof<typeof MarketPlatformEnum>;

export interface MarketLayoutNode {
  type: string; // COMPONENT_TYPE_* | SECTION_TYPE_* | SUBSECTION_*
  dataRef?: string; // Key into SortedSymbols (`data`) that this node renders. Absent when the client uses another API.
  children?: MarketLayoutNode[];
}
export interface MarketLayout {
  components: MarketLayoutNode[];
}

export interface MarketSymbolItem {
  symbol: string;
}

export interface MarketCategoryItem {
  id: string; // e.g. SYMBOL_TAG_POW
  name: string; // e.g. "PoW"
  avgPriceChangePct24h: string;
  marketCap: string;
  volume24h: string;
}

export interface SortedSymbols {
  volume: string[]; // Symbols sorted by volume
  priceChange24h: string[]; // Symbols sorted by price change 24h
  marketCap: string[]; // Symbols sorted by market cap
  newListings: string[]; // Symbols whose time of listing is within the last 28 days, sorted by time of listing (newest first)
  absFundingRates: string[];
  hotTradings: string[];
  yourFrequentTrades?: string[]; // Symbols frequently traded by the user in the last 30 days (most traded first). null when not signed in
  zeroSpread: string[]; // Symbols sorted by zero spread
  marketTrend1h: {
    up: number;
    down: number;
    neutral: number;
  };
  categories?: MarketCategoryItem[]; // Not used on web.
  trending?: MarketSymbolItem[]; // Spotlight symbols configured via Back Office (not consumed on web)
  'tradfi.all'?: MarketSymbolItem[];
  'tradfi.pre_ipo'?: MarketSymbolItem[];
  'tradfi.stock'?: MarketSymbolItem[];
  'tradfi.etf'?: MarketSymbolItem[];
  'tradfi.commodity'?: MarketSymbolItem[];
  'spot.volume'?: MarketSymbolItem[];
  // USDT 외 quote currency pair 목록은 소문자 통화 코드를 키로 내려온다.
  // 통화별로 필드를 선언하지 않고 getQuoteCurrencyPairSymbols 가 규약으로 읽는다.
  // 그 경로는 getSymbolsByDataRef 에서 Record<string, unknown> 으로 캐스팅하므로
  // 여기에 인덱스 시그니처를 열지 않는다 — 열면 선언된 필드의 오타가 컴파일 타임에 잡히지 않는다.
}

export interface GetSymbolsResponse {
  layout: MarketLayout;
  data: SortedSymbols;
}

export interface GetSymbolsParams {
  productType?: valueof<typeof InstrumentProductTypeEnum>;
  platform?: MarketPlatform;
}

/** Market home layout node type for KOL Insights (ERD §5). */
export const marketHomeComponentTypeEnum = {
  kolInsights: 'COMPONENT_TYPE_KOL_INSIGHTS',
} as const;

export const kolInsightSentimentEnum = {
  positive: 'positive',
  negative: 'negative',
  mixed: 'mixed',
} as const;
export type KolInsightSentiment = (typeof kolInsightSentimentEnum)[keyof typeof kolInsightSentimentEnum];

export const kolInsightBuzzEnum = {
  growing: 'growing',
  steady: 'steady',
} as const;
export type KolInsightBuzz = (typeof kolInsightBuzzEnum)[keyof typeof kolInsightBuzzEnum];

export interface KolInsightIndicators {
  isHot?: boolean;
  sentiment?: string;
  buzz?: string;
}

export interface KolInsightSymbol {
  symbol: string;
  mentions?: number | null;
  priceChangePct?: string | null;
  priceChangeIntervalDays?: number | null;
}

export interface KolInsight {
  title: string;
  description: string;
  mentions: number;
  sources: number;
  indicators?: KolInsightIndicators;
  symbols: KolInsightSymbol[];
}

export interface KolInsights {
  refreshedAt: string;
  windowStart?: string;
  windowEnd?: string;
  insights: KolInsight[];
}

/**
 * Flat data map for GET /v2/market/home.
 * Known KOL key is typed; other dataRef values stay open.
 */
export type MarketHomeData = {
  kolInsights?: KolInsights;
} & Record<string, unknown>;

export interface GetMarketHomeResponse {
  layout: MarketLayout;
  data: MarketHomeData;
}

export interface GetMarketHomeParams {
  productType?: valueof<typeof InstrumentProductTypeEnum>;
  platform?: MarketPlatform;
}

export const MarketStatsTypeEnum = {
  Token: 'TOKEN',
  Rwa: 'RWA',
} as const;
export type MarketStatsType = (typeof MarketStatsTypeEnum)[keyof typeof MarketStatsTypeEnum];
export interface MarketStats {
  type: MarketStatsType;
  intro: string;
  rank: number;
  marketCap: string;
  fullyDilutedMarketCap: string;
  marketDominance: string;
  volume24h: string;
  circulatingSupply: string;
  maximumSupply: string | null;
  totalSupply: string;
  allTimeHigh: string;
  allTimeLow: string;
  allTimeHighDate: string;
  allTimeLowDate: string;
}
export interface GetMarketCountryRestrictionResponse {
  isTradable: boolean;
}

export type MarketStatus =
  | 'OVER_NIGHT'
  | 'PRE_MARKET'
  | 'HOLIDAY'
  | 'MAINTENANCE'
  | 'REGULAR'
  | 'WEEKEND'
  | 'AFTER_MARKET';

export interface TradingCalendarEntry {
  timestamp: string;
  status: MarketStatus;
  /**
   * Tradable off-hours 여부. 백엔드가 세션별로 설정한다.
   * 구버전 응답에는 없을 수 있으므로 optional (없으면 off-hours 아님).
   */
  isOffHours?: boolean;
}

export type GetMarketTradingCalendarResponse = Record<string, TradingCalendarEntry[]>;
